import React, { useEffect, useState } from "react";
import PatientShareCardHeader from "./PatientShareCardHeader";
import { useSelector } from "react-redux";
import { planStageEnum } from "../RequirementsModal/constants";
import PlanDetailsTab from "./PlanDetailsTab";
import { getInstitutionUsers } from "../../../helpers/backend_api";
import { filterAvendaHelperAccounts } from "./helpers";
import ShareAndNotesTab from "./ShareAndNotesTab";
import PropTypes from "prop-types";
import Accordion from "../Accordion";
import LightBulb from "../IconComponents/LightBulb";
import LightBulbAlert from "../IconComponents/LightBulbAlert";
import RequirementList from "../RequirementList";
import { addPlanFlag } from "./helpers";
import { SUCCESS } from "../../../constants";
import { updateTreatmentPlan } from "../../../helpers/backend_api";
import { useDispatch } from "react-redux";
import { setCurrentTreatmentPlan } from "../../CreateTPlan_1Upload/actions";
import { getPlan } from "../../CreateTPlan_1Upload/helpers";
import { REQUIRED_TASKS, TAB_NAMES } from "./constants";

function Sidebar(props) {
    const dispatch = useDispatch();
    const handleCreateTreatmentPlan = (plan) => {
        dispatch(setCurrentTreatmentPlan(plan));
    };

    const authToken = useSelector((state) => state.LoginReducer.authToken);
    const userData = useSelector((state) => state.LoginReducer.userData);
    const currentTreatmentPlan = useSelector(
        (state) => state.CreatePlanReducer.currentTreatmentPlan
    );

    const [openTab, setOpenTab] = useState(TAB_NAMES.PLAN);
    const [doctorList, setDoctorList] = useState([]);
    const [hasFlag, setHasFlag] = useState(
        Boolean(currentTreatmentPlan?.Metadata?.PlanFlag?.FlagNote) || false
    );
    const [flagContent, setFlagContent] = useState(
        currentTreatmentPlan?.Metadata?.PlanFlag?.FlagNote || null
    );
    const [planStatus] = useState(
        currentTreatmentPlan?.MetaData?.PlanStage || null
    );
    const savedData = currentTreatmentPlan?.SavedData || {};

    const planCreationStep =
        savedData?.PlanCreationStep !== undefined
            ? savedData?.PlanCreationStep
            : 0;
    const planNotes =
        currentTreatmentPlan?.TreatmentPlan?.AdditionalData?.PlanNotes || {};

    useEffect(() => {
        if (userData?.institutionID && authToken) {
            getDoctorsList();
        }
    }, [userData, authToken]);

    useEffect(() => {
        if (currentTreatmentPlan?.MetaData?.PlanFlag?.FlagNote) {
            setHasFlag(true);
            setFlagContent(currentTreatmentPlan?.MetaData?.PlanFlag?.FlagNote);
        } else if (!currentTreatmentPlan?.MetaData?.PlanFlag?.FlagNote) {
            setHasFlag(false);
            setFlagContent(null);
        }
    }, [currentTreatmentPlan, doctorList]);

    function getDoctorsList() {
        if (!userData?.institutionID) return;

        getInstitutionUsers({
            userInstitutionID: userData.institutionID,
            authToken: authToken,
        }).then((resp) => {
            if (resp?.userList?.length > 0) {
                const unfilteredDrList = resp.userList
                    .filter((doctor) => doctor.uuid !== userData.uuid)
                    .map((doctor) => ({
                        firstName: doctor.firstName,
                        lastName: doctor.lastName,
                        uuid: doctor.uuid,
                        email: doctor.email,
                    }));
                const doctorList = filterAvendaHelperAccounts(
                    unfilteredDrList,
                    userData
                );
                setDoctorList(doctorList);
            }
        });
    }

    useEffect(() => {
        // If there is at least one note and the Plan Status is set to "needs review", open the Patient Note Tab
        if (
            planNotes &&
            Object.keys(planNotes).length > 0 &&
            planStatus === planStageEnum.NEEDS_REVIEW
        ) {
            setOpenTab(TAB_NAMES.NOTES);
        }
    }, []);

    function handleTabChange(tab) {
        setOpenTab(tab);
    }

    function handleTextAreaChange(e) {
        setFlagContent(e.target.value);

        const updatePlanFlagRequest = {
            token: authToken,
            patientUUID: currentTreatmentPlan?.MetaData.PatientUUID,
            planUUID: currentTreatmentPlan?.MetaData.PlanUUID,
            userUuid: userData.uuid,
            content: e.target.value,
        };

        addPlanFlag(updatePlanFlagRequest).then(() => {
            setHasFlag(true);
            getPlan(
                authToken,
                currentTreatmentPlan?.MetaData.PatientUUID,
                currentTreatmentPlan?.MetaData.PlanUUID
            )
                .then((response) => response.json())
                .then((json) => {
                    handleCreateTreatmentPlan(json.payload.plan);
                });
        });
    }

    function handleToggleFlag(e) {
        e.stopPropagation();

        if (!hasFlag) {
            setHasFlag(true);
        } else {
            setHasFlag(false);
            const updatePlanFlagRequest = {
                token: authToken,
                useruuid: userData.uuid,
                patientuuid: currentTreatmentPlan?.MetaData.PatientUUID,
                planuuid: currentTreatmentPlan?.MetaData.PlanUUID,
                plan: {
                    MetaData: {
                        PlanFlag: {},
                    },
                },
            };
            updateTreatmentPlan(updatePlanFlagRequest)
                .then((response) => response.json())
                .then((json) => {
                    if (json.message === SUCCESS) {
                        handleCreateTreatmentPlan(json.payload.plan);
                    }
                });
        }
    }
    return (
        <div
            id="patient-share-card"
            className="w-full rounded-lg pb-1 shadow-[0px_0px_8px_0px_rgba(0,0,0,0.08)]"
        >
            <PatientShareCardHeader
                openTab={openTab}
                onTabChange={handleTabChange}
                hasFlag={hasFlag}
                tooltipText={flagContent}
            />
            {openTab === TAB_NAMES.PLAN && (
                <>
                    <PlanDetailsTab
                        authToken={authToken}
                        userData={userData}
                        doctorList={doctorList}
                        handleTextAreaChange={handleTextAreaChange}
                        hasFlag={hasFlag}
                        flagContent={flagContent}
                        toggleFlag={handleToggleFlag}
                        showBiopsyTemplateSelector={
                            props.showBiopsyTemplateSelector
                        }
                    />
                    <div className="mx-4 mt-7 mb-4">
                        <Accordion
                            iconComponent={
                                props.iconComponent ? LightBulb : LightBulbAlert
                            }
                            title={REQUIRED_TASKS}
                        >
                            <RequirementList
                                requiredItems={props.requiredItems}
                                optionalItems={props.optionalItems}
                            />
                        </Accordion>
                    </div>
                </>
            )}
            {openTab === TAB_NAMES.NOTES && (
                <ShareAndNotesTab
                    currentTreatmentPlan={currentTreatmentPlan}
                    authToken={authToken}
                    userData={userData}
                    doctorList={doctorList}
                    step={planCreationStep + 1}
                />
            )}
        </div>
    );
}

Sidebar.propTypes = {
    iconComponent: PropTypes.bool,
    requiredItems: PropTypes.array,
    optionalItems: PropTypes.array,
    showBiopsyTemplateSelector: PropTypes.bool,
};

export default Sidebar;
